import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Modal } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';
import { Button, ButtonSizes, Grade } from 'atoms';
import { parseDate } from 'services/DateService';

import type { IHomeworkMutationResult, IMyLessonHomework } from 'core';

import { HomeworkAssignmentCard } from './HomeworkAssignmentCard';
import { HomeworkSubmitForm } from './HomeworkSubmitForm';
import { getHomeworkErrorText } from './homeworkErrors';

export interface IHomeworkSubmitProps {
  homework: IMyLessonHomework;
  submitLoading: boolean;
  onSubmit: (source: { githubUrl?: string; file?: File }) => Promise<IHomeworkMutationResult>;
  /** Re-download the student's own ZIP (presigned, minted per click). */
  onDownload: () => void;
  downloadLoading: boolean;
}

/**
 * Student view of the lesson homework tab: the assignment block is always
 * visible; below it either the "submitted" banner (+ grade & teacher comment
 * once graded) or the submit form. Resubmission is always allowed — after
 * grading it goes through a confirm modal (the grade will be reset).
 */
export const HomeworkSubmitComponent = memo(
  ({ homework, submitLoading, onSubmit, onDownload, downloadLoading }: IHomeworkSubmitProps) => {
    const { t } = useTranslation();
    const [replacing, setReplacing] = useState(false);
    const [errorCode, setErrorCode] = useState<string | null>(null);

    const submission = homework.mySubmission;
    // canSubmit=false → the server rejects every submission from this viewer
    // (free-group visitor, terminated agreement). Showing the form would be a
    // guaranteed dead end, so the tab stays read-only.
    const formVisible = homework.canSubmit && (!submission || replacing);

    const handleSubmit = useCallback(
      async (source: { githubUrl?: string; file?: File }): Promise<IHomeworkMutationResult> => {
        setErrorCode(null);
        try {
          const result = await onSubmit(source);
          if (result.success) {
            setReplacing(false);
          } else {
            setErrorCode(result.errorCode || 'GENERIC');
          }
          return result;
        } catch {
          setErrorCode('NETWORK');
          return { success: false, errorCode: 'NETWORK' };
        }
      },
      [onSubmit],
    );

    const onReplaceClick = useCallback(() => {
      if (submission?.isGraded) {
        Modal.confirm({
          title: `${t('homework.submit.replaceConfirmTitle')}`,
          content: `${t('homework.submit.replaceConfirmText')}`,
          okText: `${t('homework.submit.replaceConfirmOk')}`,
          cancelText: `${t('homework.submit.replaceConfirmCancel')}`,
          onOk: () => setReplacing(true),
        });
        return;
      }
      setReplacing(true);
    }, [submission?.isGraded, t]);

    return (
      <Wrapper>
        <HomeworkAssignmentCard assignment={homework.assignment} />

        {submission && (
          <SubmittedBanner>
            <SubmittedHeader>
              <SubmittedTitle>{t('homework.status.submitted')}</SubmittedTitle>
              {/* Until a grade lands, "сдано" alone left the student unable to
                  tell "the teacher hasn't looked yet" from "nothing will ever
                  happen" — say which it is. */}
              {!submission.isGraded && (
                <PendingChip>{t('homework.status.underReview')}</PendingChip>
              )}
            </SubmittedHeader>
            <SubmittedMeta>
              {t('homework.submit.attempt', { attempt: submission.attempt })} ·{' '}
              {parseDate(submission.submittedAt)}
            </SubmittedMeta>
            {/* rev.3: a submission can carry a link AND a file — show both. */}
            {submission.githubUrl && (
              <WorkLink href={submission.githubUrl} target="_blank" rel="noopener noreferrer">
                {submission.githubUrl}
              </WorkLink>
            )}
            {submission.fileName && (
              <FileRow>
                <SubmittedMeta>
                  {t('homework.submit.kindZip')}: {submission.fileName}
                </SubmittedMeta>
                <DownloadButton
                  variant="outline"
                  size={ButtonSizes.sm}
                  colors={{ borderColor: Colors.gray }}
                  isLoading={downloadLoading}
                  disabled={downloadLoading}
                  onClick={onDownload}>
                  {t('homework.submit.download')}
                </DownloadButton>
              </FileRow>
            )}

            {submission.isGraded && submission.hwMark && (
              <GradeBlock>
                <GradeTitle>{t('homework.grade.title')}</GradeTitle>
                <Grade gradeType={submission.hwMark} />
                {submission.teacherComment && (
                  <>
                    <CommentTitle>{t('homework.grade.comment')}</CommentTitle>
                    <CommentText>{submission.teacherComment}</CommentText>
                  </>
                )}
              </GradeBlock>
            )}

            {!replacing && homework.canSubmit && (
              <ReplaceButton
                variant="outline"
                size={ButtonSizes.sm}
                colors={{ borderColor: Colors.gray }}
                onClick={onReplaceClick}>
                {t('homework.submit.replace')}
              </ReplaceButton>
            )}
          </SubmittedBanner>
        )}

        {!homework.canSubmit && (
          <AlertStyled type="info" showIcon message={`${t('homework.submit.cannotSubmit')}`} />
        )}

        {formVisible && (
          <>
            {errorCode && (
              <AlertStyled type="error" showIcon message={getHomeworkErrorText(t, errorCode)} />
            )}
            <HomeworkSubmitForm
              submitLoading={submitLoading}
              onSubmit={handleSubmit}
              onCancel={submission ? () => setReplacing(false) : undefined}
            />
          </>
        )}
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  width: 100%;

  ${tablet} {
    max-width: 700px;
  }
`;

const SubmittedBanner = styled.div`
  margin-top: 24px;
  border: 1px solid ${Colors.additional_gray};
  border-radius: 8px;
  padding: 20px;

  ${tablet} {
    padding: 24px;
  }
`;

const SubmittedHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const PendingChip = styled.span`
  padding: 2px 10px;
  border-radius: 999px;
  background-color: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const DownloadButton = styled(Button)`
  width: auto;
`;

const SubmittedTitle = styled.p`
  margin: 0 0 4px;
  ${fontService.getFont({ size: 16, lineHeight: 150, weight: FontWeight.bold })}
`;

const SubmittedMeta = styled.p`
  margin: 0 0 4px;
  color: ${Colors.gray};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  word-break: break-all;
`;

const WorkLink = styled.a`
  display: inline-block;
  margin: 0 0 4px;
  word-break: break-all;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const GradeBlock = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${Colors.additional_gray};
`;

const GradeTitle = styled.p`
  margin: 0 0 8px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })}
`;

const CommentTitle = styled.p`
  margin: 12px 0 4px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })}
`;

const CommentText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const ReplaceButton = styled(Button)`
  margin-top: 16px;
`;

const AlertStyled = styled(Alert)`
  margin-top: 24px;
`;
